"use client";

/**
 * Outros eventos, at the end of an event's page (montanhas-web#16): the next few
 * by date as a strip of small cards scrolled sideways on a phone -- a row on a
 * desktop -- each with its banner, the label before its title when given, when it
 * is, its title, how hard and what it costs, and Lista de espera aberta at its foot
 * when the event is full and its list can be joined; then the way to every event
 * in Explore, so that every event leads on to the next ones. Início drew it too,
 * as Próximos eventos, until montanhas-web#21 gave it UpcomingEvents instead.
 */
import Link from "next/link";
import Image from "next/image";
import { ArrowRight } from "lucide-react";
import { TAB_PATHS, eventPath, inscricaoPath } from "@/lib/address";
import { cdnUrl } from "@/lib/cdn";
import { euros, shortWhen } from "@/lib/format";
import { buttonClass } from "@/components/ui/Button";
import { difficultyLevelLabel, trackSelectItem } from "@/components/explore/eventCardModel";
import { SectionHead } from "@/components/home/pieces";
import { TitleLabel, type Label } from "@/components/home/useLiveLabels";
import { bannerRatio, leadPhoto, type HomeEvent } from "@/components/home/model";

// what GA is told the list is called (listNameFor, with a way of its own)
export const STRIP_LIST = "Início · Próximos";

export function UpcomingStrip({
  events,
  labels = {},
  going = {},
  waiting = {},
  title = "Próximos eventos",
  list = STRIP_LIST,
  className = "",
}: {
  events: HomeEvent[];
  labels?: Record<number, Label | null>;
  going?: Record<number, boolean>;
  /** the events whose card says Lista de espera aberta */
  waiting?: Record<number, boolean>;
  title?: string;
  /** what GA is told the list is called */
  list?: string;
  className?: string;
}) {
  if (!events.length) return null;
  return (
    <section className={className}>
      <SectionHead title={title} more={{ href: TAB_PATHS.explore, label: "Ver todos" }} />
      <div className="scroll-x -mx-4 flex gap-2.5 px-4 pb-2.5 pt-0.5 desk:mx-0 desk:grid desk:grid-cols-3 desk:gap-5 desk:overflow-visible desk:px-0 lg:grid-cols-4">
        {events.map((event) => (
          <MiniCard key={event.id} event={event} label={labels[event.id]} going={!!going[event.id]} waiting={!!waiting[event.id]} list={list} />
        ))}
      </div>
      <Link href={TAB_PATHS.explore} className={buttonClass("outline", "md", true, "mt-1 desk:hidden")}>
        Ver todos os eventos <ArrowRight size={17} />
      </Link>
    </section>
  );
}

function MiniCard({ event, label, going, waiting, list }: { event: HomeEvent; label: Label | null | undefined; going: boolean; waiting: boolean; list: string }) {
  const photo = leadPhoto(event);
  const href = going ? inscricaoPath(event) : eventPath(event);
  const price = Number(event.price) || 0;
  const difficulty = difficultyLevelLabel(event.type === "CIRCLE" ? null : event.difficulty);
  return (
    <Link href={href} onClick={() => trackSelectItem(event as never, list)} className="card pressable flex w-[168px] flex-none snap-start flex-col overflow-hidden desk:w-auto desk:transition-[transform,box-shadow] desk:duration-200 desk:hover:-translate-y-1 desk:hover:shadow-float">
      <span className="relative block bg-brand-mint" style={{ aspectRatio: bannerRatio(event) }}>
        {photo && <Image src={cdnUrl(photo.thumbnail || photo.path)} alt={String(event.title || "").trim()} fill sizes="(min-width: 40rem) 300px, 168px" className="object-cover" />}
      </span>
      <span className="block px-2.5 pb-3 pt-2.5">
        <small className="block text-[11.5px] font-bold text-brand-dark">{shortWhen(event.start_time)}</small>
        <h3 className="my-0.5 line-clamp-2 min-h-[34px] text-[13.5px] font-bold leading-[1.25] desk:text-[15px]">
          <TitleLabel label={label} small />
          {String(event.title || "").trim()}
        </h3>
        <span className="flex items-baseline justify-between text-[12px] text-ink-soft">
          <span>{difficulty}</span>
          {price > 0 && <b className="text-[14px] text-price">{euros(price)}</b>}
        </span>
      </span>
      {waiting && <span className="mt-auto block border-t border-line bg-brand-mint px-2 py-1.5 text-center text-[11.5px] font-bold leading-tight text-[#256b43]">Lista de espera aberta</span>}
    </Link>
  );
}
